import { readIdentity, singleRead } from "./readFlight";
import { accountRequestSucceeded } from './authRecoveryState';
import { rememberRequest } from './requestDiagnostics';
import type {
  CloudQueueControl,
  ReplyMessage,
  ReplyTodo,
  ReplyTodoPage,
  Device,
  Lead,
  LeadSourceBatchSummary,
  Notification,
  PageResult,
  Project,
  KeywordGroup,
  MessageGroup,
  Task,
  TaskCatalogItem,
  TargetBatch,
  ContactList,
  TargetBatchItem,
  DeviceQueue,
} from "../types";

import { aiDisplayText } from "./ai-display";
import { businessPath } from "./businessRouting";
import { businessStep, rememberConnection } from './connectionDiagnostics';
import { localDeviceHeaders } from './localDeviceAccess';
import type { GeneratedSource } from './projectSettings';
type QueryValue = string | number | boolean | null | undefined;

export function formatApiErrorDetail(detail: unknown): string | null {
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => formatApiErrorDetail(item))
      .filter((item): item is string => Boolean(item));
    return messages.length ? Array.from(new Set(messages)).join("；") : null;
  }
  if (detail && typeof detail === "object") {
    const value = detail as Record<string, unknown>;
    for (const key of ["msg", "message", "detail"]) {
      const rendered = formatApiErrorDetail(value[key]);
      if (rendered) return rendered.replace(/^Value error,\s*/i, "");
    }
  }
  return null;
}

export interface MobileControlSettings {
  runtime: string;
  device_type: "physical_phone" | "emulator";
  connection_provider: "native";
  native_service_url: string;
  max_parallel_devices: number;
  token_configured: boolean;
  password_configured: boolean;
  last_test_status: string;
  last_test_message: string;
  last_tested_at: string | null;
}

export interface WorkspacePhone extends DeviceQueue { queue_paused: boolean; manual_control: boolean; }

export interface MobileProviderStatus {
  provider_id: "native";
  device_type: "physical_phone" | "emulator";
  status:
    | "ready"
    | "unconfigured"
    | "not_installed"
    | "unavailable"
    | "degraded";
  message: string;
  actions: string[];
  detail: Record<string, unknown>;
  ready: boolean;
}

export interface PhoneControlStatus {
  active: boolean;
  candidate_enabled?: boolean;
  manual_generation: string | null;
  manual_epoch: string | null;
}

export interface PhoneTakeoverStartResult {
  device_id: string;
  status: string;
  manual_takeover: true;
  manual_generation: string;
  manual_epoch?: string | null;
}

export interface PhoneTakeoverEndResult {
  device_id: string;
  status: string;
  manual_takeover: false;
  manual_generation: null;
  manual_epoch?: string | null;
  business_resumed?: false;
}

function phoneTakeover(deviceId:string,active:true):Promise<PhoneTakeoverStartResult>;
function phoneTakeover(deviceId:string,active:false,expectedGeneration:string):Promise<PhoneTakeoverEndResult>;
function phoneTakeover(deviceId:string,active:boolean,expectedGeneration?:string){
  if(!active&&(!expectedGeneration||!expectedGeneration.trim()))return Promise.reject(new Error("缺少当前人工控制代际，未发送结束请求"));
  return request<PhoneTakeoverStartResult|PhoneTakeoverEndResult>(`/devices/${deviceId}/manual-takeover/${active?"start":"end"}`,{
    method:"POST",
    ...(active?{}:{body:JSON.stringify({expected_generation:expectedGeneration})}),
  });
}

function queryString(values: Record<string, QueryValue>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== null && value !== "")
      params.set(key, String(value));
  }
  const rendered = params.toString();
  return rendered ? `?${rendered}` : "";
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  if ((init?.method || 'GET').toUpperCase() !== 'GET') return requestOnce<T>(path, init);
  const headers = await localDeviceHeaders(path);
  const supplied = Array.from(new Headers(init?.headers).entries()).sort();
  const normalized = new URL(path, window.location.origin);
  normalized.searchParams.sort();
  const key = JSON.stringify([normalized.pathname + normalized.search, supplied, headers, init?.credentials || 'same-origin']);
  return singleRead(key, () => requestOnce<T>(path, {...init, signal: undefined}), init?.signal || undefined);
}

async function requestOnce<T>(path: string, init?: RequestInit): Promise<T> {
  const identity = readIdentity();
  const diagnosticStart=performance.now();
  let diagnosticStatus:number|null=null, diagnosticCode='ok', diagnosticError='none';
  try {
  const recoveryOperation = /^\/(?:v2\/devices\/[^/]+\/(?:queue-control|patrol-schedules)|v2\/queue-task-config\/confirm|agent-runs\/(?:plan(?:\/[^/]+)?|preview|confirm))$/.test(path);
  const deviceHeaders=await localDeviceHeaders(path);
  let target: string;
  try {
    target = Object.keys(deviceHeaders).length ? path : await businessPath(path);
  } catch (error) {
    diagnosticError='other';diagnosticCode='other';
    rememberConnection(businessStep(path), 'routing');
    throw error;
  }
  let response: Response;
  try {
    response = await fetch(`/api${target}`, {
      ...init,
      // Final confirmation has a server-side 30s *read* wait plus bounded
      // connect/write/pool/auth phases. Do not abort that whole operation at 30s.
      signal: init?.signal ?? (recoveryOperation && path !== '/v2/queue-task-config/confirm' ? AbortSignal.timeout(30_000) : undefined),
      cache: 'no-store',
      headers: { "Content-Type": "application/json", ...init?.headers, ...deviceHeaders },
    });
  } catch (error) {
    diagnosticError='transport';
    rememberConnection(businessStep(path), 'fetch');
    throw error;
  }
  diagnosticStatus=response.status;
  if (!response.ok) {
    diagnosticError='http_status';
    let message = `请求失败（HTTP ${response.status}）`;
    let restartAllowed = false;
    let code = '';
    try {
      const payload = (await response.json()) as {
        detail?: unknown;
        message?: unknown;
        code?: string;
      };
      code = payload.code || '';
      diagnosticCode=code;
      if (code === 'AUTH_UNAVAILABLE') rememberConnection(businessStep(path), 'http_status', response.status);
      message =
        formatApiErrorDetail(payload.detail) ??
        formatApiErrorDetail(payload.message) ??
        message;
      restartAllowed = Boolean(payload.detail && typeof payload.detail === "object" && "restart_allowed" in payload.detail && payload.detail.restart_allowed === true);
    } catch {
      // Keep safe generic message for non-JSON errors.
      rememberConnection(businessStep(path), 'json_parse', response.status);
    }
    if (code === 'SERVICE_BUSY') message = '服务器繁忙，请稍后重试';
    if (code === 'AUTH_UNAVAILABLE') message = '暂时无法连接账号服务，请稍后再试。';
    if (code === 'AUTH_UNAVAILABLE' || response.status === 401 || response.status === 403 && ['SIGNED_OUT','SESSION_REPLACED','SESSION_REVOKED','SESSION_EXPIRED','ACCOUNT_DISABLED','ACCOUNT_EXPIRED','APP_NOT_GRANTED','APP_DISABLED','DEVICE_REVOKED'].includes(code))
      window.dispatchEvent(new Event("account-access-invalid"));
    throw Object.assign(new Error(aiDisplayText(message)), {restartAllowed, status: response.status, code});
  }
  try {
    const result = (await response.json()) as T;
    if (!/^\/(?:health|client-architecture)(?:[/?]|$)/.test(path)) accountRequestSucceeded(identity);
    return result;
  } catch {
    diagnosticError='json_parse';
    rememberConnection(businessStep(path), 'json_parse', response.status);
    throw new Error("服务返回了无效数据，请重启本机服务后重试；若仍失败，请联系维护人员。");
  }
  } finally { rememberRequest(path,init?.method || 'GET',diagnosticStatus,diagnosticCode,diagnosticError,performance.now()-diagnosticStart); }
}

export const api = {
  openPhoneWindow: () => request<{ok:boolean;message:string}>("/phone-workspace/window",{method:"POST"}),
  phoneWorkspace: () => request<WorkspacePhone[]>("/device-queues"),
  phoneQueueCommand: (id: string, action: "pause" | "resume" | "clear", revision: number) => request<{changed:number;errors:string[];paused:boolean}>(`/phone-workspace/${id}/queue`, {method:"POST", body:JSON.stringify({action, revision})}),
  cloudQueueCommand: (id:string,action:'pause'|'resume'|'stop',expectedRevision:string,requestId:string) =>
    request<CloudQueueControl>(`/v2/devices/${encodeURIComponent(id)}/queue-control`,{method:'POST',body:JSON.stringify({action,expected_revision:expectedRevision,request_id:requestId})}),
  cloudQueueStatus: (id:string) => request<CloudQueueControl>(`/v2/devices/${encodeURIComponent(id)}/queue-control`),
  preparePhone: (id: string) => request<{state:string;message?:string;installed?:boolean}>(`/phone-workspace/${id}/prepare`, {method:"POST"}),
  testPhoneConnection: (id: string) => request<{ok:boolean;message:string;tested_at:string}>(`/phone-workspace/${id}/test`, {method:"POST"}),
  recoverPhone: (id: string) => request<{state:"ready"|"busy"|"unauthorized"|"missing"|"offline"|"error"|"outcome_unknown";message:string;connection_detail:"ready"|"unauthorized"|"offline"|"missing"|"error";recovery_scope:"all_offline"|"none";affected_serials?:string[]|null;attempted?:number;tested_at?:string}>(`/phone-workspace/${id}/recover`, {method:"POST"}),
  replyRecordAction: (id: string, action: "done") => request<ReplyTodo>(`/v2/reply-todos/${id}/record-action`, {method:"POST", body:JSON.stringify({action})}),
  generatePlatformResources: (projectId: string, platform: string, requestId?: string) =>
    request<{ source?: GeneratedSource; quality?: {complete: boolean; missing_slots: string[]}; tasks: { catalog_id: string; keywords?: string[]; messages?: string[]; messages_by_purpose?: Partial<Record<MessageGroup["purpose"], string[]>> }[] }>(`/v2/projects/${projectId}/platforms/${platform}/generate-resources`, { method: "POST", ...(requestId ? {headers:{"Idempotency-Key":requestId}} : {}) }),
  resourceTaskCatalog: () => request<TaskCatalogItem[]>("/task-resource-catalog").then(rows => rows.filter(item => item.task_action !== "blogger_acquisition")),
  notifications: () =>
    request<{
      items: Notification[];
      unread: number;
      attention_count: number;
      unresolved_count: number;
      notifications_enabled?: boolean;
    }>("/notifications"),
  readNotification: (id: string) =>
    request(`/notifications/${id}/read`, { method: "POST" }),
  dismissNotification: (id: string) =>
    request(`/notifications/${encodeURIComponent(id)}/dismiss`, { method: "POST" }),
  deviceQueues: (environment: "live" | "demo" = "live") =>
    request<DeviceQueue[]>(`/device-queues${queryString({ environment })}`),
  reorderDeviceQueue: (
    deviceId: string,
    taskIds: string[],
    revision?: number,
  ) =>
    request(`/device-queues/${deviceId}/order`, {
      method: "PUT",
      body: JSON.stringify({ task_ids: taskIds, revision }),
    }),
  moveTask: (
    taskId: string,
    deviceId: string,
    sourceRevision: number,
    destinationRevision: number,
  ) =>
    request(`/tasks/${taskId}/move`, {
      method: "POST",
      body: JSON.stringify({
        device_id: deviceId,
        source_revision: sourceRevision,
        destination_revision: destinationRevision,
      }),
    }),
  updateQueuedTask: (taskId: string, payload: Record<string, unknown>) =>
    request<Task>(`/tasks/${taskId}/queue-settings`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  projects: (status?: string) =>
    request<Project[]>(`/v2/projects${queryString({ status })}`),
  createProject: (payload: Record<string, unknown>) =>
    request<Project>("/v2/projects", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateProject: (id: string, payload: Record<string, unknown>) =>
    request<Project>(`/v2/projects/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  keywordGroups: (projectId?: string, platform?: string) =>
    request<KeywordGroup[]>(
      `/v2/keyword-groups${queryString({ project_id: projectId, platform })}`,
    ),
  messageGroups: (projectId?: string, platform?: string) =>
    request<MessageGroup[]>(
      `/v2/message-groups${queryString({ project_id: projectId, platform })}`,
    ),
  saveTaskKeywords: (payload: Record<string, unknown>) =>
    request<KeywordGroup>("/v2/task-keyword-group", { method: "PUT", body: JSON.stringify(payload) }),
  saveTaskMessages: (payload: Record<string, unknown>) =>
    request<MessageGroup>("/v2/task-message-group", { method: "PUT", body: JSON.stringify(payload) }),
  keywordHistory: (projectId: string, catalogId: string) =>
    request<{ items: Array<{ task_id: string; task_name: string; created_at: string; keyword: string; status: string; displayed: number | null; new_ids: number | null; duplicates: number | null }> }>(`/v2/keyword-history?project_id=${encodeURIComponent(projectId)}&catalog_id=${encodeURIComponent(catalogId)}`),
  replyTodos: (filters: Record<string, QueryValue> = {}) =>
    request<ReplyTodoPage>(`/v2/reply-todos${queryString(filters)}`),
  replyMessages: (id: string) =>
    request<ReplyMessage[]>(`/v2/reply-todos/${id}/messages`),
  targetBatches: (platform?: string, environment?: string) =>
    request<TargetBatch[]>(
      `/target-batches${queryString({ platform, environment, limit: 100 })}`,
    ),
  contactLists: (filters: Record<string, QueryValue>) => request<PageResult<ContactList>>(`/contact-lists${queryString(filters)}`),
  removeTargetBatchItem: (batchId: string, itemId: string) => request<{removed: boolean}>(`/v2/target-batches/${encodeURIComponent(batchId)}/items/${encodeURIComponent(itemId)}`, {method:"DELETE"}),
  contactListMembers: (id: string, page: number) => request<PageResult<TargetBatchItem>>(`/contact-lists/${id}/members${queryString({page})}`),
  setContactListTarget: (id: string, enabled: boolean) => request<ContactList>(`/contact-lists/${id}`, {method: "PATCH", body: JSON.stringify({status: enabled ? "ready" : "draft"})}),
  deleteContactList: (id: string) => request<{id: string; status: string}>(`/contact-lists/${id}`, {method: "DELETE"}),
  leads: (filters: Record<string, QueryValue>) =>
    request<PageResult<Lead>>(`/leads${queryString(filters)}`),
  leadSourceBatches: (filters: Record<string, QueryValue> = {}) =>
    request<LeadSourceBatchSummary[]>(
      `/lead-source-batches${queryString(filters)}`,
    ),
  lead: (id: string) => request<Lead>(`/leads/${id}`),
  contactAgain: (id:string, payload:{action:"direct_message"|"add_wechat_friend";version:number;request_id:string}) =>
    request<{id:string;expires_at:number;single_use:boolean}>(`/leads/${id}/contact-again`,{method:"POST",body:JSON.stringify(payload)}),
  updateLead: (id: string, payload: Record<string, unknown>) =>
    request<Lead>(`/leads/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  bulkLeads: (payload: Record<string, unknown>) =>
    request<{ message: string }>("/leads/bulk", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  approveBotTask: (taskId: string) =>
    request<Task>(`/bot-tasks/${taskId}/approve`, { method: "POST" }),

  phoneTakeover,
  phoneAction: (deviceId: string, action: string, parameters: Record<string, unknown>, diagnostic?: {session_id:string;generation:string;action_id:string;channel:"existing_rpc"|"scrcpy_control"}, manual_candidate?:{session_id:string;generation:string;action_id:string;browser_instance:string}) => request<{ok:boolean;input_applied?:boolean;input_replaced?:boolean;error?:string;elapsed_ms?:number;state?:string;reason?:string;channel?:string;action_id?:string;generation?:string;business_completed?:boolean}>(`/devices/${deviceId}/manual-action`, {method: "POST", body: JSON.stringify({action, parameters, ...(diagnostic?{diagnostic}:{}),...(manual_candidate?{manual_candidate}:{})})}),
  phoneControlStatus: (deviceId: string) => request<PhoneControlStatus>(`/devices/${deviceId}/manual-control`),
  phoneInputStatus: (deviceId: string) => request<{installed:boolean;state:string;message?:string;version?:number|null;required_version?:number;checked_at?:number;reason?:string}>(`/devices/${deviceId}/input-component`),
  phoneInstallInput: (deviceId: string) => request(`/devices/${deviceId}/input-component`,{method:"POST"}),
  devices: (status?: string) =>
    request<Device[]>(`/devices${queryString({ status })}`),
  deleteDevice: (id:string) => request<{ok:boolean}>(`/devices/${id}`,{method:"DELETE"}),
  scheduleHistory: (id:string) => request<Array<{date:string;count:number;apply_supported?:boolean;tasks:Array<{id:string;name:string;platform:string;total:number;run_mode:string;schedule_at:string|null;external_actions:boolean}>}>>(`/device-queues/${id}/history`),
  updateDevice: (id: string, payload: Record<string, unknown>) =>
    request<Device>(`/devices/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  refreshDevices: () =>
    request<{ message: string }>("/devices/refresh", { method: "POST" }),
  tasks: (filters: Record<string, QueryValue>) =>
    request<PageResult<Task>>(`${filters.work_records_only ? '/work-records' : '/tasks'}${queryString(filters)}`),
  task: (id: string, workRecord = false) => request<Task>(`${workRecord ? '/work-records' : '/tasks'}/${id}`),
  taskAction: (
    id: string,
    action: "pause" | "resume" | "stop" | "cancel" | "terminate",
    workRecord = false,
  ) => request<Task>(`${workRecord ? '/work-records' : '/tasks'}/${id}/${action}`, { method: "POST" }),
  socialCategories: () =>
    request<{ categories: string[] }>("/settings/social-categories"),
  updateSocialCategories: (categories: string[]) =>
    request<{ categories: string[] }>("/settings/social-categories", {
      method: "PATCH",
      body: JSON.stringify({ categories }),
    }),
  runtimeSettings: () =>
    request<Record<string, number | boolean>>("/settings/runtime"),
  updateRuntimeSettings: (payload: Record<string, unknown>) =>
    request<Record<string, number | boolean>>("/settings/runtime", {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  mobileSettings: () => request<MobileControlSettings>("/settings/mobile"),
  mobileProviderStatus: () =>
    request<MobileProviderStatus>("/settings/mobile/provider-status"),
  testMobileConnection: () =>
    request<{
      ok: boolean;
      status: string;
      message: string;
      detail: Record<string, unknown>;
    }>("/settings/mobile/test", { method: "POST" }),
  leadSummary: (environment?: string) =>
    request<{ total: number; with_phone: number; without_phone: number }>(
      `/leads-summary${queryString({ environment })}`,
    ),
};

export async function exportLeads(
  options: {
    leadIds?: string[];
    filters?: Record<string, QueryValue>;
    environment?: string;
    sourceTaskId?: string;
  } = {},
): Promise<void> {
  const query = queryString({
    ...(options.filters ?? {}),
    lead_ids: options.leadIds?.join(","),
    environment: options.environment ?? options.filters?.environment,
    source_task_id: options.sourceTaskId ?? options.filters?.source_task_id,
  });
  const path=await businessPath(`/leads/export.csv${query}`);
  if(!path.startsWith('/sales-cloud/')) { window.location.assign(`/api${path}`); return; }
  const value=await request<{filename:string;content:string;content_type:string}>(path);
  const url=URL.createObjectURL(new Blob([value.content],{type:value.content_type}));
  const link=document.createElement('a');link.href=url;link.download=value.filename;
  document.body.appendChild(link);link.click();link.remove();
  window.setTimeout(()=>URL.revokeObjectURL(url),1000);
}



export function exportTargetBatch(id: string): void {
  window.location.assign(`/api/target-batches/${id}/export.csv`);
}
